import { Controller } from "@hotwired/stimulus"

// Searchable dropdown that ENHANCES a native <select> (Hotwire-only, no JS
// libraries). The select stays in the form — it still carries the value, the
// `required` rule and the server's pre-selection, and it is what shows if this
// script never runs — but it is hidden behind one text field: click or type to
// filter, arrows to move, Enter / Tab / click to pick, Escape to back out.
//
//   <div data-controller="combobox">
//     <select data-combobox-target="select" …>…</select>
//   </div>
//
// Validation stays with the select (form_validation_controller reads it); this
// controller only mirrors the red border onto the field the user can see and
// hands focus to it.
const MAX_RENDERED = 200
const OPTION_CLASS = "flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-slate-700 dark:text-slate-200"
const ACTIVE_CLASSES = ["bg-brand-50", "text-brand-800", "dark:bg-slate-700", "dark:text-white"]
let uid = 0

export default class extends Controller {
  static targets = ["select"]
  static values = { placeholder: { type: String, default: "Type to search…" } }

  connect() {
    // A Turbo cache snapshot can come back with the previous visit's field.
    this.element.querySelectorAll("[data-combobox-built]").forEach((node) => node.remove())

    this.select = this.selectTarget
    this.id = `combobox-${++uid}`
    this.element.classList.add("relative")
    this.build()
    this.syncFromSelect()

    this.onSelectChange = () => this.syncFromSelect()
    this.onSelectFocus = () => this.input.focus()
    this.onSelectInvalid = () => this.markInvalid(true)
    this.select.addEventListener("change", this.onSelectChange)
    this.select.addEventListener("focus", this.onSelectFocus)
    this.select.addEventListener("invalid", this.onSelectInvalid)
  }

  disconnect() {
    this.select.removeEventListener("change", this.onSelectChange)
    this.select.removeEventListener("focus", this.onSelectFocus)
    this.select.removeEventListener("invalid", this.onSelectInvalid)
    this.select.classList.remove("sr-only")
    this.select.removeAttribute("tabindex")
    this.select.removeAttribute("aria-hidden")
  }

  build() {
    const select = this.select
    const label = select.labels?.[0]?.textContent.replace(/\*/g, "").trim() ||
                  select.getAttribute("aria-label") || ""

    this.input = document.createElement("input")
    this.input.type = "text"
    this.input.className = `${select.className} pr-9`
    this.input.placeholder = this.placeholderValue
    this.input.autocomplete = "off"
    this.input.spellcheck = false
    this.input.dataset.comboboxBuilt = ""
    this.input.setAttribute("role", "combobox")
    this.input.setAttribute("aria-autocomplete", "list")
    this.input.setAttribute("aria-expanded", "false")
    this.input.setAttribute("aria-controls", `${this.id}-list`)
    if (label) this.input.setAttribute("aria-label", label)
    if (select.disabled) this.input.disabled = true

    this.chevron = document.createElement("span")
    this.chevron.dataset.comboboxBuilt = ""
    this.chevron.setAttribute("aria-hidden", "true")
    this.chevron.className = "pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400"
    this.chevron.innerHTML = '<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clip-rule="evenodd"/></svg>'

    this.list = document.createElement("ul")
    this.list.id = `${this.id}-list`
    this.list.dataset.comboboxBuilt = ""
    this.list.setAttribute("role", "listbox")
    if (label) this.list.setAttribute("aria-label", label)
    this.list.hidden = true
    this.list.className = "absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200 " +
                          "bg-white py-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-800"

    // The chevron sits on the input, so wrap the two; the list hangs below.
    this.field = document.createElement("div")
    this.field.dataset.comboboxBuilt = ""
    this.field.className = "relative"
    this.field.append(this.input, this.chevron)
    select.insertAdjacentElement("beforebegin", this.field)
    this.field.insertAdjacentElement("afterend", this.list)

    select.classList.add("sr-only")
    select.tabIndex = -1
    select.setAttribute("aria-hidden", "true")

    this.input.addEventListener("focus", () => this.open())
    this.input.addEventListener("click", () => this.open())
    this.input.addEventListener("input", () => { this.typed = true; this.open() })
    this.input.addEventListener("keydown", (event) => this.keydown(event))
    this.input.addEventListener("blur", () => this.commitOnBlur())
    // mousedown, not click: picking must happen before the input's blur.
    this.list.addEventListener("mousedown", (event) => {
      event.preventDefault()
      const item = event.target.closest("[role=option]")
      if (item) this.choose(item.dataset.value)
    })
  }

  // ---- options ---------------------------------------------------------

  choices() {
    return Array.from(this.select.options)
      .filter((option) => option.value !== "" && !option.disabled)
      .map((option) => ({
        value: option.value,
        label: option.textContent.trim(),
        group: option.parentElement.tagName === "OPTGROUP" ? option.parentElement.label : null
      }))
  }

  matches() {
    const words = this.typed ? this.input.value.toLowerCase().split(/\s+/).filter(Boolean) : []
    return this.choices().filter((choice) => {
      const text = `${choice.label} ${choice.group || ""}`.toLowerCase()
      return words.every((word) => text.includes(word))
    })
  }

  render() {
    const found = this.matches()
    const shown = found.slice(0, MAX_RENDERED)
    this.list.replaceChildren()
    this.items = []

    if (shown.length === 0) {
      const empty = document.createElement("li")
      empty.className = "px-3 py-2 text-slate-500 dark:text-slate-400"
      empty.textContent = "No matches"
      this.list.append(empty)
    }

    let group = null
    shown.forEach((choice, index) => {
      if (choice.group && choice.group !== group) {
        group = choice.group
        const heading = document.createElement("li")
        heading.setAttribute("role", "presentation")
        heading.className = "px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400"
        heading.textContent = group
        this.list.append(heading)
      }
      const item = document.createElement("li")
      const selected = choice.value === this.select.value
      item.id = `${this.id}-opt-${index}`
      item.setAttribute("role", "option")
      item.setAttribute("aria-selected", selected ? "true" : "false")
      item.dataset.value = choice.value
      item.className = OPTION_CLASS
      const text = document.createElement("span")
      text.className = "truncate"
      text.textContent = choice.label
      item.append(text)
      if (selected) {
        const tick = document.createElement("span")
        tick.className = "text-brand-600 dark:text-brand-400"
        tick.setAttribute("aria-hidden", "true")
        tick.textContent = "✓"
        item.append(tick)
      }
      this.list.append(item)
      this.items.push(item)
    })

    if (found.length > shown.length) {
      const more = document.createElement("li")
      more.className = "px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
      more.textContent = `${found.length - shown.length} more — keep typing to narrow the list`
      this.list.append(more)
    }

    const current = this.items.findIndex((item) => item.dataset.value === this.select.value)
    this.activate(this.typed ? 0 : Math.max(current, 0))
  }

  activate(index) {
    if (!this.items || this.items.length === 0) {
      this.active = -1
      this.input.removeAttribute("aria-activedescendant")
      return
    }
    this.active = Math.min(Math.max(index, 0), this.items.length - 1)
    this.items.forEach((item, i) => ACTIVE_CLASSES.forEach((name) => item.classList.toggle(name, i === this.active)))
    const item = this.items[this.active]
    this.input.setAttribute("aria-activedescendant", item.id)
    item.scrollIntoView({ block: "nearest" })
  }

  // ---- open / close / pick --------------------------------------------

  open() {
    if (this.input.disabled) return
    this.render()
    this.list.hidden = false
    this.input.setAttribute("aria-expanded", "true")
  }

  close() {
    this.list.hidden = true
    this.input.setAttribute("aria-expanded", "false")
    this.input.removeAttribute("aria-activedescendant")
  }

  choose(value) {
    this.typed = false
    if (this.select.value !== value) {
      this.select.value = value
      this.select.dispatchEvent(new Event("input", { bubbles: true }))
      this.select.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.syncFromSelect()
    this.close()
  }

  keydown(event) {
    const isOpen = !this.list.hidden
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        isOpen ? this.activate(this.active + 1) : this.open()
        break
      case "ArrowUp":
        event.preventDefault()
        if (isOpen) this.activate(this.active - 1)
        break
      case "Enter":
        // Never submit the form from here — pick instead.
        if (isOpen) {
          event.preventDefault()
          if (this.active >= 0) this.choose(this.items[this.active].dataset.value)
        }
        break
      case "Tab":
        if (isOpen && this.typed && this.active >= 0) this.choose(this.items[this.active].dataset.value)
        break
      case "Escape":
        if (isOpen) {
          event.preventDefault()
          this.typed = false
          this.syncFromSelect()
          this.close()
        }
        break
    }
  }

  // Leaving the field: an emptied box clears the choice; anything else that
  // was typed but not picked snaps back to what is actually selected, so the
  // box never shows a name the form is not going to send.
  commitOnBlur() {
    if (this.input.value.trim() === "" && this.select.value !== "") {
      this.choose("")
    } else {
      this.typed = false
      this.syncFromSelect()
    }
    this.close()
  }

  syncFromSelect() {
    const option = this.select.selectedOptions[0]
    this.input.value = option && option.value !== "" ? option.textContent.trim() : ""
    if (this.select.value !== "") this.markInvalid(false)
  }

  markInvalid(invalid) {
    if (invalid) {
      this.input.setAttribute("aria-invalid", "true")
      this.input.style.borderColor = "#dc2626"
    } else if (this.input.hasAttribute("aria-invalid")) {
      this.input.removeAttribute("aria-invalid")
      this.input.style.borderColor = ""
    }
  }
}
